import React, { memo, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import Stack from '@mui/system/Stack';
import { KpiBenchmarks } from 'benchmarking-domain/kpi-benchmark';
import { KpiDefinition } from 'benchmarking-domain/kpi-definition';
import { KpiStatistics, KpiValues } from 'benchmarking-domain/kpi-value';
import { PlantKpiHistory } from 'benchmarking-domain/plant-kpi-history';
import Project from 'benchmarking-domain/project';
import { useDebugMode } from 'hooks/use-debug-mode';

import KpiChartDistribution from './kpi-chart-distribution';
import KpiChartQuartiles from './kpi-chart-quartiles';
import KpiChartSiteHistory from './kpi-chart-site-history';

enum KpiDisplayMode {
  Quartiles = 'Quartiles',
  Distribution = 'Distribution',
  SiteHistory = 'Site History',
}

const fullScreenStyles: SxProps = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: 10000,
  padding: 5,
  background: '#fff',
};

export interface KpiDetailsProps {
  project: Project;
  scopeKpiValues: KpiValues | undefined;
  kpi: KpiDefinition;
  benchmarks?: KpiBenchmarks | undefined;
  statistics?: Map<string, KpiStatistics> | undefined;
  historyItems?: Map<string, PlantKpiHistory> | undefined;
}

const KpiDetails: React.FC<KpiDetailsProps> = memo(
  ({ project, scopeKpiValues, kpi, benchmarks, statistics, historyItems }) => {
    const [displayMode, setDisplayMode] = useState<KpiDisplayMode>(KpiDisplayMode.Quartiles);

    const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);

    const isDebugMode = useDebugMode();

    const handleDisplayModelChange = (event: React.MouseEvent<HTMLElement>, newDisplayMode: KpiDisplayMode | null) => {
      if (newDisplayMode !== null) {
        setDisplayMode(newDisplayMode);
      }
    };

    const renderChart = (kpi: KpiDefinition, displayMode: KpiDisplayMode) => {
      switch (displayMode) {
        case KpiDisplayMode.Quartiles:
          return (
            <KpiChartQuartiles
              project={project}
              scopeKpiValues={scopeKpiValues}
              kpi={kpi}
              benchmarks={benchmarks}
              statistics={statistics}
            />
          );
        case KpiDisplayMode.Distribution:
          return (
            <KpiChartDistribution project={project} scopeKpiValues={scopeKpiValues} kpi={kpi} statistics={statistics} />
          );
        case KpiDisplayMode.SiteHistory:
          return (
            <KpiChartSiteHistory
              project={project}
              scopeKpiValues={scopeKpiValues}
              kpi={kpi}
              historyItems={historyItems}
            />
          );
        default:
          return null;
      }
    };

    return (
      <Stack spacing={1} alignItems="flex-start" sx={fullScreenMode ? fullScreenStyles : {}}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap={'wrap'}>
          <Typography variant="h6">{kpi.name}</Typography>
          <Tooltip title={kpi.description}>
            <IconButton aria-label={`Info about ${kpi.name}`}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
          {isDebugMode && (
            <Stack direction="column" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {kpi.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {kpi.calculationPhrase
                  ? kpi.calculationPhrase.replace(']/[', '] / [')
                  : 'Calculation Phrase NOT DEFINED'}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          <ToggleButtonGroup
            color="primary"
            value={displayMode}
            exclusive
            onChange={handleDisplayModelChange}
            aria-label="Display Mode"
            size="small"
            sx={{ alignSelf: 'center' }}
          >
            <ToggleButton value={KpiDisplayMode.Quartiles} sx={{ textTransform: 'none' }}>
              Quartiles
            </ToggleButton>
            <ToggleButton value={KpiDisplayMode.Distribution} sx={{ textTransform: 'none' }}>
              Distribution
            </ToggleButton>
            <ToggleButton value={KpiDisplayMode.SiteHistory} sx={{ textTransform: 'none' }}>
              Site History
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            size="small"
            title={fullScreenMode ? 'Close fullscreen mode' : 'Open fullscreen mode'}
            onClick={() => setFullScreenMode(!fullScreenMode)}
          >
            {fullScreenMode ? <ZoomOutIcon /> : <ZoomInIcon />}
          </Button>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%', marginBottom: -2 }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px', fontStyle: 'italic' }}>
            {KpiDefinition.getScoringDescription(kpi.whatIsBetter)}
          </Typography>
          <Tooltip title={kpi.unit}>
            <Typography variant="body2" color="text.secondary" noWrap={true}>
              Units: {kpi.unit}
            </Typography>
          </Tooltip>
        </Stack>

        <Box
          sx={{
            display: 'block',
            position: 'relative',
            width: '100%',
            height: fullScreenMode ? '100%' : '300px',
            marginBottom: 2,
          }}
        >
          {renderChart(kpi, displayMode)}
        </Box>
      </Stack>
    );
  }
);

export default KpiDetails;
